// Small building blocks shared across sections. Values come straight from the
// handoff (README "Iconography", "Spacing"). Colours reference the CSS
// variables defined in index.css.
import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconKey } from '../lib/icons';

/** The content column: max-width 640, 20px side padding. */
export const COL: CSSProperties = { maxWidth: 640, margin: '0 auto', padding: '0 20px' };
export const MONO = 'var(--font-mono)';

/** Medallion: 44–58px circle, 2px accent border, transparent fill. */
export function Medallion({
  icon,
  size = 44,
  iconSize = 24,
  color = 'var(--cyan)',
  border,
}: {
  icon: IconKey;
  size?: number;
  iconSize?: number;
  color?: string;
  border?: string;
}) {
  return (
    <div
      style={{
        width: size,
        height: size,
        flex: 'none',
        borderRadius: '50%',
        border: `2px solid ${border ?? color}`,
        display: 'grid',
        placeItems: 'center',
        color,
      }}
    >
      <Icon name={icon} size={iconSize} />
    </div>
  );
}

/** Tile: 48px square, 12px radius, faint cyan fill, no border. */
export function IconTile({ icon }: { icon: IconKey }) {
  return (
    <div
      style={{
        width: 48,
        height: 48,
        flex: 'none',
        borderRadius: 12,
        background: 'rgba(127,227,232,0.12)',
        display: 'grid',
        placeItems: 'center',
        color: 'var(--cyan)',
      }}
    >
      <Icon name={icon} size={26} />
    </div>
  );
}

/** Section numeral (orange mono) + h2. */
export function SectionHead({ numeral, title }: { numeral: string; title: string }) {
  return (
    <>
      <div
        style={{
          fontFamily: MONO,
          fontSize: 12,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--orange)',
        }}
      >
        {numeral}
      </div>
      <h2
        style={{
          fontSize: 'clamp(32px,8vw,42px)',
          fontWeight: 900,
          letterSpacing: '-0.04em',
          lineHeight: 1,
          margin: '16px 0 24px',
          color: 'var(--bone)',
        }}
      >
        {title}
      </h2>
    </>
  );
}

/** A section wrapper with the standard rhythm + scroll-reveal hook. */
export function Section({
  label,
  children,
  id,
  style,
}: {
  label: string;
  id?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <section
      id={id}
      data-reveal=""
      data-screen-label={label}
      style={{ padding: '56px 0 8px', ...style }}
    >
      {children}
    </section>
  );
}
